
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { ApolloQueryResult } from '@apollo/client';

import {
  GetInternalTransfersQuery,
  GetInternalTransfersDocument,
  TransferDetail,
  ETransactionStatus
} from '@/modules/common/services/graphql/server/server.sdk';
import {
  Heading,
  NavBar,
  Row,
  StatusBar,
  BaseModal,
  ButtonPrimary,
  HelperView,
  LoaderIndicator
} from '@warrenbrasil/nebraska-web';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { CloseButton } from '@/modules/common/components/buttons';
import { getString } from '@/modules/common/helpers/resources';

const customerModule = namespace('CustomerModule');

interface IStep {
  title: string;
  description: string;
}

enum ETransactionSteps {
  Requested = 1,
  Processing = 2,
  Accepted = 3
}

@Component({
  components: {
    LoaderIndicator,
    BaseModal,
    CloseButton,
    Heading,
    NavBar,
    StatusBar,
    Row,
    HelperView,
    ButtonPrimary
  }
})
export default class LegacyTransferDetails extends Vue {
  @customerModule.Getter('getCustomerId')
  private customerId!: string;

  @Prop({ type: String, required: true })
  private id!: string;

  private hasError = false;
  private isLoading = true;
  private transfer!: TransferDetail;

  private userIsWhitelabel = !!localStorage.getItem(
    'warren.themes_cache.whitelabel.name'
  );

  private NebraskaColors = NebraskaColors;

  private steps: IStep[] = [];

  beforeMount() {
    this.loadReceiptDetails();
  }

  @Watch('id')
  private async loadReceiptDetails() {
    if (this.id) {
      try {
        const { data }: ApolloQueryResult<GetInternalTransfersQuery> =
          await this.$apollo.query({
            query: GetInternalTransfersDocument,
            variables: {
              customerId: this.customerId,
              transactionId: this.id
            }
          });
        const transfer = data!.transaction!.transfer as TransferDetail;
        this.transfer = transfer;
        this.steps = [
          {
            title: getString(
              'portfolio_transaction_status_transfer_requested_step1_title'
            ),
            description: transfer!.createdDate!.formatted
          },
          {
            title: this.isProcessing(transfer)
              ? getString(
                  'portfolio_transaction_status_transfer_processing_step2_title'
                )
              : getString(
                  'portfolio_transaction_status_transfer_requested_step2_title'
                ),
            description: this.isProcessing(transfer)
              ? getString(
                  'portfolio_transaction_status_transfer_processing_step2_description'
                )
              : getString(
                  'portfolio_transaction_status_transfer_requested_step2_description'
                )
          },
          {
            title: getString(
              'portfolio_transaction_status_transfer_requested_step3_title'
            ),
            description: transfer!.date!.formatted
          }
        ];
      } catch (e) {
        this.hasError = true;
      } finally {
        this.isLoading = false;
      }
    }
  }

  private isProcessing(transferData: TransferDetail) {
    return transferData!.status!.value === ETransactionStatus.Processing;
  }

  private get getCurrentStep() {
    switch (this.transfer.status?.value) {
      case ETransactionStatus.Accepted:
        return ETransactionSteps.Accepted;
      case ETransactionStatus.Processing:
        return ETransactionSteps.Processing;
      default:
        return ETransactionSteps.Requested;
    }
  }
}
